import * as v from "valibot";

export const DEFAULT_RUNTIME_CONFIG_GLOBAL = "__INTERMEDES_RUNTIME_CONFIG__";
export const DEFAULT_RUNTIME_CONFIG_SCRIPT_ID = "__intermedes-runtime-config";

export type RuntimeConfigSchema = v.GenericSchema<Record<string, unknown>>;
export type RuntimeConfigOutput<TSchema extends RuntimeConfigSchema> = v.InferOutput<TSchema>;
export type RuntimeConfigAliases<TConfig extends Record<string, unknown>> = Record<string, keyof TConfig>;

const browserConfigCache = new Map<string, unknown>();
const isBuildTime = () =>
  process.env.NEXT_PHASE === "phase-production-build" || process.env.npm_lifecycle_event === "build";
export const isRuntimeConfigBuildTime = isBuildTime;

const buildTimePlaceholder = (key: string) => {
  if (/DATABASE_URL/i.test(key)) return "postgres://build-runtime-config.invalid/build";
  if (/BETTER_AUTH_SECRET|JWT_SECRET|SECRET/i.test(key)) {
    return "build-time-placeholder-secret-value-that-is-long-enough-for-library-validation";
  }
  if (/PORT$/i.test(key)) return "0";
  if (/URL$/i.test(key) || /Url$/.test(key)) return "https://build-runtime-config.invalid/";
  if (/ENABELD|ENABLED/i.test(key)) return "false";
  return "build-time-placeholder";
};

export class RuntimeConfigError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "RuntimeConfigError";
  }
}

export const parseRuntimeConfig = <TSchema extends RuntimeConfigSchema>(
  schema: TSchema,
  input: unknown,
  appName = "runtime-config",
): RuntimeConfigOutput<TSchema> => {
  const result = v.safeParse(schema, input);

  if (!result.success) {
    const fields = v.flatten(result.issues).nested;
    throw new RuntimeConfigError(`Invalid ${appName} runtime config: ${JSON.stringify(fields)}`);
  }

  return result.output;
};

export const escapeRuntimeConfigJson = (value: unknown) =>
  JSON.stringify(value).replaceAll("<", "\\u003c").replaceAll("\u2028", "\\u2028").replaceAll("\u2029", "\\u2029");

export const runtimeConfigAssignment = (config: unknown, globalName = DEFAULT_RUNTIME_CONFIG_GLOBAL) =>
  `window.${globalName} = ${escapeRuntimeConfigJson(config)};`;

const readGlobalConfig = (globalName: string) => {
  if (typeof window === "undefined") {
    throw new RuntimeConfigError("Browser runtime config can only be read in a browser context");
  }

  return (window as unknown as Record<string, unknown>)[globalName];
};

export const readBrowserRuntimeConfig = <TSchema extends RuntimeConfigSchema>(
  schema: TSchema,
  options?: {
    appName?: string;
    globalName?: string;
  },
): RuntimeConfigOutput<TSchema> => {
  const globalName = options?.globalName ?? DEFAULT_RUNTIME_CONFIG_GLOBAL;
  const cacheKey = `${options?.appName ?? "runtime-config"}:${globalName}`;
  const cached = browserConfigCache.get(cacheKey);
  if (cached) return cached as RuntimeConfigOutput<TSchema>;

  const raw = readGlobalConfig(globalName);
  const parsed = parseRuntimeConfig(schema, raw, options?.appName);
  browserConfigCache.set(cacheKey, parsed);

  return parsed;
};

export const readRuntimeConfigValue = (configKey: string, serverEnvName = configKey) => {
  if (typeof window !== "undefined") {
    const config = (window as unknown as Record<string, Record<string, unknown> | undefined>)[
      DEFAULT_RUNTIME_CONFIG_GLOBAL
    ];
    const value = config?.[configKey];
    return typeof value === "string" ? value : undefined;
  }

  return process.env[serverEnvName];
};

export const createRuntimeEnvProxy = <
  TSchema extends RuntimeConfigSchema,
  TConfig extends RuntimeConfigOutput<TSchema> & Record<string, unknown>,
  TAliases extends RuntimeConfigAliases<TConfig>,
>({
  schema,
  getServerConfig,
  aliases,
  appName,
  globalName,
}: {
  schema: TSchema;
  getServerConfig: () => TConfig;
  aliases: TAliases;
  appName?: string;
  globalName?: string;
}) => {
  type ProxyShape = {
    readonly [K in keyof TAliases]: TConfig[TAliases[K]];
  };

  const readConfig = () => {
    if (typeof window === "undefined") return getServerConfig();
    return readBrowserRuntimeConfig(schema, { appName, globalName }) as TConfig;
  };

  return new Proxy(
    {},
    {
      get(_target, property) {
        if (typeof property !== "string") return undefined;
        const configKey = aliases[property];
        if (!configKey) return undefined;
        if (typeof window === "undefined" && isBuildTime()) return buildTimePlaceholder(String(configKey));
        return readConfig()[configKey];
      },
      has(_target, property) {
        return typeof property === "string" && property in aliases;
      },
      ownKeys() {
        return Object.keys(aliases);
      },
      getOwnPropertyDescriptor(_target, property) {
        if (typeof property !== "string" || !(property in aliases)) return undefined;
        return { enumerable: true, configurable: true };
      },
    },
  ) as ProxyShape;
};

export const createLazyServerEnv = <TSchema extends RuntimeConfigSchema>({
  schema,
  runtimeEnv = () => process.env,
  appName = "server-env",
  emptyStringAsUndefined = true,
}: {
  schema: TSchema;
  runtimeEnv?: () => Record<string, unknown>;
  appName?: string;
  emptyStringAsUndefined?: boolean;
}) => {
  type EnvShape = RuntimeConfigOutput<TSchema>;
  let cached: EnvShape | undefined;

  const read = () => {
    if (cached) return cached;

    const raw = runtimeEnv();
    const input = emptyStringAsUndefined
      ? Object.fromEntries(Object.entries(raw).map(([key, value]) => [key, value === "" ? undefined : value]))
      : raw;

    cached = parseRuntimeConfig(schema, input, appName);
    return cached;
  };

  return new Proxy(
    {},
    {
      get(_target, property) {
        if (typeof property !== "string") return undefined;
        if (isBuildTime()) {
          const value = runtimeEnv()[property];
          return value === "" || value == null ? buildTimePlaceholder(property) : value;
        }
        return (read() as Record<string, unknown>)[property];
      },
      has(_target, property) {
        if (isBuildTime()) return typeof property === "string";
        return typeof property === "string" && property in read();
      },
      ownKeys() {
        if (isBuildTime()) return Reflect.ownKeys(runtimeEnv());
        return Reflect.ownKeys(read());
      },
      getOwnPropertyDescriptor(_target, property) {
        if (isBuildTime() && typeof property === "string") return { enumerable: true, configurable: true };
        if (typeof property !== "string" || !(property in read())) return undefined;
        return { enumerable: true, configurable: true };
      },
    },
  ) as EnvShape;
};
